import { useState } from 'react';
import { ShieldCheck, WarningCircle } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ESignature - Componente reutilizable de firma electrónica 21 CFR Part 11
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSigned: (signature) => void  — se ejecuta solo tras verificar PIN correctamente
 *  - action: string — descripción de la acción (ej: "Completar cuestionario")
 *  - resource: string — identificador del recurso (ej: "EST-0068/VOL-0194/T0/Q1")
 *  - lang: 'es' | 'en'
 */
export default function ESignature({ open, onClose, onSigned, onSubmitPin, action, resource, lang = 'es' }) {
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);

  const lb = lang === 'es'
    ? { title: 'Firma Electronica Requerida', desc: 'Introduzca su PIN de firma y el motivo para confirmar esta accion.', pin: 'PIN de firma', reason: 'Motivo', reason_placeholder: 'Ej: Revisado y conforme, Datos verificados...', sign: 'Firmar', cancel: 'Cancelar', pin_required: 'El PIN es obligatorio', reason_required: 'El motivo es obligatorio', pin_wrong: 'PIN incorrecto', action_label: 'Accion' }
    : { title: 'Electronic Signature Required', desc: 'Enter your signature PIN and reason to confirm this action.', pin: 'Signature PIN', reason: 'Reason', reason_placeholder: 'E.g: Reviewed and approved, Data verified...', sign: 'Sign', cancel: 'Cancel', pin_required: 'PIN is required', reason_required: 'Reason is required', pin_wrong: 'Incorrect PIN', action_label: 'Action' };

  const handleSign = async () => {
    setError('');
    if (!pin.trim()) { setError(lb.pin_required); return; }
    if (!reason.trim()) { setError(lb.reason_required); return; }
    setVerifying(true);
    try {
      // onSubmitPin: el propio endpoint de la accion verifica el PIN en servidor.
      if (onSubmitPin) {
        await onSubmitPin(pin.trim(), reason.trim());
        setPin('');
        setReason('');
        setVerifying(false);
        return;
      }
      const { data } = await axios.post(`${API_URL}/api/auth/verify-signature`, {
        pin: pin.trim(), reason: reason.trim(), action, resource,
      }, { withCredentials: true });
      setPin('');
      setReason('');
      onSigned(data.signature);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error';
      setError(detail);
    }
    setVerifying(false);
  };

  const handleClose = () => {
    setPin('');
    setReason('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck size={22} className="text-[#1f3e80]" />
            {lb.title}
          </DialogTitle>
          <DialogDescription>{lb.desc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-stone-50 border border-stone-200 rounded-sm px-3 py-2">
            <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">{lb.action_label}</p>
            <p className="text-sm text-stone-800 font-medium">{action}</p>
          </div>

          <div className="space-y-1.5">
            <Label>{lb.pin} <span className="text-red-500">*</span></Label>
            <Input
              data-testid="esign-pin"
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setError(''); }}
              placeholder="••••••"
              className="text-center text-lg tracking-[0.5em] font-mono"
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label>{lb.reason} <span className="text-red-500">*</span></Label>
            <Textarea
              data-testid="esign-reason"
              value={reason}
              onChange={e => { setReason(e.target.value); setError(''); }}
              placeholder={lb.reason_placeholder}
              className="min-h-[50px] text-sm"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-2.5 flex items-center gap-2" data-testid="esign-error">
              <WarningCircle size={14} className="text-red-500 shrink-0" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleClose}>{lb.cancel}</Button>
          <Button
            data-testid="esign-confirm-btn"
            onClick={handleSign}
            disabled={verifying}
            className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-1.5"
          >
            <ShieldCheck size={16} />
            {verifying ? '...' : lb.sign}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
